import { BoardApi } from "../../../services/board-service-api";
import { BOARD_CLIENT_ID } from "./board-client-id";
import {
  ArtifactKind,
  ArtifactLink,
  Attachment,
  AutomationAction,
  AutomationRule,
  AutomationTrigger,
  Board,
  BoardChangeEvent,
  BoardColumn,
  BoardMode,
  BoardTransition,
  BoardScope,
  BoardStatus,
  CreateIssueInput,
  CustomFieldDef,
  CustomFieldType,
  Issue,
  IssueCard,
  IssueComment,
  IssueType,
  IssueLink,
  IssueLinkType,
  LabelDef,
  MoveIssueInput,
  Sprint,
  UpdateIssueInput,
  Version
} from "./types";

// Backend-backed mutation facade (no client cache). The board is EF-persisted in CoreApi
// (v1/api/board) and read on demand: bounded config via /config, paged issue cards per
// column, and a per-issue detail bundle — there is NO full-board snapshot held here. Each
// mutation calls the REST endpoint and then fires a signal; on-demand views (columns, detail,
// roadmap) subscribe and re-fetch their own slice. refresh(configChanged) additionally fires
// the narrower config signal when a mutation alters the bounded config.

// Attachments are base64 data URLs; the cap mirrors the backend's 3 MB limit and
// is surfaced in the UI before upload.
export const MAX_ATTACHMENT_BYTES: number = 3 * 1024 * 1024;

// The card-visible subset of an UpdateIssueInput, used to patch the card/store in place
// BEFORE the round-trip so a field edit reflects instantly. Detail-only fields
// (description, estimates, custom fields) and statusId are intentionally excluded: the
// former never appear on a card, and a statusId change is a column move that the
// patch-in-place path can't express (handled by the authoritative reload instead).
function optimisticCardPatch(input: UpdateIssueInput): Partial<IssueCard> {
  const patch: Partial<IssueCard> = {};
  if (input.summary !== undefined) patch.summary = input.summary;
  if (input.type !== undefined) patch.type = input.type;
  if (input.priority !== undefined) patch.priority = input.priority;
  if (input.assigneeUserId !== undefined) patch.assigneeUserId = input.assigneeUserId;
  if (input.labels !== undefined) patch.labels = input.labels;
  if (input.storyPoints !== undefined) patch.storyPoints = input.storyPoints;
  if (input.startDate !== undefined) patch.startDate = input.startDate;
  if (input.dueDate !== undefined) patch.dueDate = input.dueDate;
  if (input.sprintId !== undefined) patch.sprintId = input.sprintId;
  if (input.parentIssueId !== undefined) patch.parentIssueId = input.parentIssueId;
  if (input.epicId !== undefined) patch.epicId = input.epicId;
  if (input.fixVersionId !== undefined) patch.fixVersionId = input.fixVersionId;
  return patch;
}

// The card-relevant subset of a mutation's Issue response, used to patch a column card in
// place (no refetch). Count/rank/blocked fields aren't on Issue and don't change on a field
// edit, so the existing card keeps them.
function issueToCardPatch(issue: Issue): Partial<IssueCard> {
  return {
    summary: issue.summary,
    type: issue.type,
    statusId: issue.statusId,
    priority: issue.priority,
    assigneeUserId: issue.assigneeUserId,
    labels: issue.labels,
    storyPoints: issue.storyPoints,
    startDate: issue.startDate,
    dueDate: issue.dueDate,
    parentIssueId: issue.parentIssueId,
    epicId: issue.epicId,
    sprintId: issue.sprintId,
    fixVersionId: issue.fixVersionId,
    updatedAt: issue.updatedAt
  };
}

// A mutation signal — fired after every board change so on-demand views (columns, detail,
// roadmap, …) re-fetch their own slice. Carries a typed BoardChangeEvent so subscribers can
// react only to the kinds/ids that touch their slice instead of blindly reloading.
export interface BoardServiceListener {
  (event: BoardChangeEvent): void;
}

// Config listeners take no payload — they only fire on "config.changed".
export interface BoardConfigListener {
  (): void;
}

class BoardServiceImpl {
  private listeners: Set<BoardServiceListener> = new Set();
  // Separate, narrower signal: fired only when a mutation changes the bounded config
  // (statuses, boards, labels, sprints, versions, custom-field defs, automations, projects).
  // The config store subscribes here so frequent issue edits don't trigger a /config refetch.
  private configListeners: Set<BoardConfigListener> = new Set();

  subscribe(listener: BoardServiceListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Subscribe to config-only changes (see configListeners above).
  subscribeConfig(listener: BoardConfigListener): () => void {
    this.configListeners.add(listener);
    return () => {
      this.configListeners.delete(listener);
    };
  }

  // Fired after every LOCAL mutation: notify in-process subscribers so each view re-fetches (or
  // patches) only its affected slice. Propagation to OTHER tabs is server-authoritative — the
  // backend publishes the event from inside the mutation request (BoardEventPublishingFilter),
  // excluding this tab by the X-Board-Client-Id header it sends — so there is nothing to relay here.
  async refresh(event: BoardChangeEvent): Promise<void> {
    this.notify(event);
  }

  // Apply an event that arrived from another tab over the SSE stream: notify subscribers so
  // their slice reconciles, but never re-emit. The originating tab is already excluded
  // server-side by clientId; the self-guard here is belt-and-suspenders.
  applyRemote(event: BoardChangeEvent): void {
    if (event.originClientId !== undefined && event.originClientId === BOARD_CLIENT_ID) return;
    this.notify(event);
  }

  // Notify in-process subscribers, and wake the config store on a "config.changed" event.
  private notify(event: BoardChangeEvent): void {
    this.emit(event);
    if (event.kind === "config.changed") {
      for (const listener of this.configListeners) listener();
    }
  }

  // Owner-only board onboarding: create default columns + link platform projects, then
  // refresh so the now-populated board renders.
  async initializeBoard(): Promise<void> {
    try {
      await BoardApi.initialize();
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  // ── Issues ───────────────────────────────────────────────────────────

  async createIssue(input: CreateIssueInput): Promise<Issue | null> {
    try {
      const issue: Issue = await BoardApi.createIssue(input);
      await this.refresh({ kind: "issue.created", issueId: issue.id, statusId: issue.statusId });
      return issue;
    } catch {
      return null;
    }
  }

  async cloneIssue(issueId: string): Promise<Issue | null> {
    try {
      const issue: Issue = await BoardApi.cloneIssue(issueId);
      await this.refresh({ kind: "issue.created", issueId: issue.id, statusId: issue.statusId });
      return issue;
    } catch {
      return null;
    }
  }

  async updateIssue(issueId: string, patch: UpdateIssueInput): Promise<Issue | null> {
    // Optimistic pass: reflect card-visible field edits in the columns/store immediately,
    // before the round-trip. Skipped when the edit changes the column (a move, which
    // patch-in-place can't express) or touches no card field (description/estimates/custom
    // fields live only in the detail, which already echoes them locally).
    const optimistic: Partial<IssueCard> = optimisticCardPatch(patch);
    const canOptimize: boolean = patch.statusId === undefined && Object.keys(optimistic).length > 0;
    if (canOptimize) {
      this.emit({ kind: "issue.updated", issueId, patch: optimistic, optimistic: true });
    }
    try {
      const issue: Issue = await BoardApi.updateIssue(issueId, patch);
      await this.refresh({
        kind: "issue.updated",
        issueId: issue.id,
        statusId: issue.statusId,
        patch: issueToCardPatch(issue)
      });
      return issue;
    } catch {
      // Roll the optimistic patch back: a patch-less issue.updated reloads the affected
      // views (and the store) to server truth.
      if (canOptimize) await this.refresh({ kind: "issue.updated", issueId });
      return null;
    }
  }

  async deleteIssue(issueId: string): Promise<void> {
    try {
      await BoardApi.deleteIssue(issueId);
      await this.refresh({ kind: "issue.deleted", issueId });
    } catch {
      // toasted
    }
  }

  async moveIssue(input: MoveIssueInput): Promise<Issue | null> {
    // The grid columns own their own (server-paginated) state and reload on the emit
    // signal, so the move just hits the API and emits — no central cache to update.
    try {
      const issue: Issue = await BoardApi.moveIssue(input);
      await this.refresh({
        kind: "issue.moved",
        issueId: issue.id,
        toStatusId: issue.statusId,
        fromStatusId: input.sourceStatusId
      });
      return issue;
    } catch {
      // Reconcile both columns back to server truth after a failed optimistic move.
      await this.refresh({
        kind: "issue.moved",
        issueId: input.issueId,
        toStatusId: input.destinationStatusId,
        fromStatusId: input.sourceStatusId
      });
      return null;
    }
  }

  async archiveIssue(issueId: string): Promise<void> {
    try {
      await BoardApi.archiveIssue(issueId);
      await this.refresh({ kind: "issue.archived", issueId });
    } catch {
      // toasted
    }
  }

  async restoreIssue(issueId: string): Promise<void> {
    try {
      await BoardApi.restoreIssue(issueId);
      await this.refresh({ kind: "issue.restored", issueId });
    } catch {
      // toasted
    }
  }

  async toggleWatch(issueId: string): Promise<void> {
    try {
      await BoardApi.toggleWatch(issueId);
      await this.refresh({ kind: "issue.updated", issueId });
    } catch {
      // toasted
    }
  }

  async createSubtask(parentIssueId: string, summary: string): Promise<Issue | null> {
    try {
      const issue: Issue = await BoardApi.createSubtask(parentIssueId, summary);
      await this.refresh({ kind: "issue.subtaskChanged", issueId: parentIssueId });
      return issue;
    } catch {
      return null;
    }
  }

  async promoteSubtask(subtaskId: string, parentIssueId?: string): Promise<boolean> {
    try {
      const promoted: boolean = await BoardApi.promoteSubtask(subtaskId);
      // Promotion makes the subtask a top-level board card (unknown landing column → conservative
      // create) AND removes it from its parent's subtask list. Emit both so the columns surface
      // the new card and the parent's open detail drops it from its subtask list.
      await this.refresh({ kind: "issue.created", issueId: subtaskId });
      if (parentIssueId) await this.refresh({ kind: "issue.subtaskChanged", issueId: parentIssueId });
      return promoted;
    } catch {
      return false;
    }
  }

  async toggleSubtaskComplete(subtaskId: string): Promise<void> {
    try {
      await BoardApi.toggleSubtaskComplete(subtaskId);
    } catch {
      // toasted
    }
    // Reconcile unconditionally so the optimistic toggle in the open detail settles to (or rolls
    // back to) server truth.
    await this.refresh({ kind: "issue.subtaskChanged" });
  }

  // ── Comments ─────────────────────────────────────────────────────────

  async addComment(issueId: string, bodyMarkdown: string, id?: string): Promise<IssueComment | null> {
    const trimmed: string = bodyMarkdown.trim();
    if (!trimmed) return null;
    try {
      const comment: IssueComment = await BoardApi.addComment(issueId, trimmed, id);
      await this.refresh({ kind: "comment.changed", issueId });
      return comment;
    } catch {
      return null;
    }
  }

  // issueId is the comment's issue, passed by the open detail so the event targets only that
  // issue's detail instead of reloading whichever detail happens to be open.
  //
  // The detail panel patches its cached bundle optimistically before calling these, so the
  // refresh runs unconditionally (after success OR failure): it confirms the change on success
  // and restores server truth — rolling the optimistic patch back — on failure.
  async editComment(commentId: string, bodyMarkdown: string, issueId?: string): Promise<boolean> {
    const trimmed: string = bodyMarkdown.trim();
    if (!trimmed) return false;
    let ok: boolean = false;
    try {
      ok = await BoardApi.editComment(commentId, trimmed);
    } catch {
      // toasted
    }
    await this.refresh({ kind: "comment.changed", issueId });
    return ok;
  }

  async deleteComment(commentId: string, issueId?: string): Promise<void> {
    try {
      await BoardApi.deleteComment(commentId);
    } catch {
      // toasted
    }
    await this.refresh({ kind: "comment.changed", issueId });
  }

  // Toggle an emoji reaction on a comment. The open detail patches its cached bundle
  // optimistically, so the refresh runs unconditionally — confirming on success, rolling back
  // on failure.
  async toggleCommentReaction(commentId: string, emoji: string, issueId?: string): Promise<void> {
    try {
      await BoardApi.toggleCommentReaction(commentId, emoji);
    } catch {
      // toasted
    }
    await this.refresh({ kind: "comment.changed", issueId });
  }

  // ── Links ────────────────────────────────────────────────────────────

  // Read-only predicate — no refresh.
  async wouldCreateBlockingCycle(fromIssueId: string, toIssueId: string, type: IssueLinkType): Promise<boolean> {
    try {
      return await BoardApi.wouldCreateBlockingCycle(fromIssueId, toIssueId, type);
    } catch {
      return false;
    }
  }

  // add*/remove* below reconcile unconditionally (the open detail patches its bundle
  // optimistically): the refresh confirms on success and rolls back on failure.
  async addLink(fromIssueId: string, toIssueId: string, type: IssueLinkType): Promise<IssueLink | null> {
    let link: IssueLink | null = null;
    try {
      link = await BoardApi.addLink(fromIssueId, toIssueId, type);
    } catch {
      // toasted
    }
    await this.refresh({ kind: "links.changed", issueId: fromIssueId });
    return link;
  }

  async removeLink(linkId: string): Promise<void> {
    try {
      await BoardApi.removeLink(linkId);
    } catch {
      // toasted
    }
    await this.refresh({ kind: "links.changed" });
  }

  // ── Boards ───────────────────────────────────────────────────────────

  async createBoard(name: string, scope: BoardScope, mode: BoardMode = "scrum"): Promise<Board | null> {
    try {
      const board: Board = await BoardApi.createBoard(name, scope, mode);
      await this.refresh({ kind: "config.changed" });
      return board;
    } catch {
      return null;
    }
  }

  async renameBoard(boardId: string, name: string): Promise<void> {
    try {
      await BoardApi.renameBoard(boardId, name);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async deleteBoard(boardId: string): Promise<void> {
    try {
      await BoardApi.deleteBoard(boardId);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async setBoardMode(boardId: string, mode: BoardMode): Promise<void> {
    try {
      await BoardApi.setBoardMode(boardId, mode);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  // Replace the board's whole column set (create / rename / reorder / assign-statuses in one call).
  async setBoardColumns(boardId: string, columns: BoardColumn[]): Promise<void> {
    try {
      await BoardApi.setBoardColumns(boardId, columns);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async setWipLimit(boardId: string, columnId: string, limit: number | null): Promise<void> {
    try {
      await BoardApi.setWipLimit(boardId, columnId, limit);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  // ── Sprints ──────────────────────────────────────────────────────────

  async createSprint(projectKey: string, name: string, goal: string = ""): Promise<Sprint | null> {
    try {
      const sprint: Sprint = await BoardApi.createSprint(projectKey, name, goal);
      await this.refresh({ kind: "config.changed" });
      return sprint;
    } catch {
      return null;
    }
  }

  async startSprint(sprintId: string, endDate: string | null = null): Promise<Sprint | null> {
    try {
      await BoardApi.startSprint(sprintId, endDate);
      await this.refresh({ kind: "config.changed" });
      return null;
    } catch {
      return null;
    }
  }

  async completeSprint(sprintId: string): Promise<Sprint | null> {
    try {
      await BoardApi.completeSprint(sprintId);
      await this.refresh({ kind: "config.changed" });
      return null;
    } catch {
      return null;
    }
  }

  async deleteSprint(sprintId: string): Promise<void> {
    try {
      await BoardApi.deleteSprint(sprintId);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async assignIssueToSprint(issueId: string, sprintId: string | null): Promise<void> {
    // sprintId is a card field — reflect it instantly, then reconcile (or roll back) on the
    // authoritative refresh that always runs below.
    this.emit({ kind: "issue.updated", issueId, patch: { sprintId }, optimistic: true });
    try {
      await BoardApi.assignIssueToSprint(issueId, sprintId);
    } catch {
      // toasted
    }
    await this.refresh({ kind: "issue.updated", issueId });
  }

  // ── Projects ─────────────────────────────────────────────────────────

  async renameProject(key: string, name: string): Promise<void> {
    try {
      await BoardApi.renameProject(key, name);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async deleteProject(key: string): Promise<void> {
    try {
      await BoardApi.deleteProject(key);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async setProjectTransitions(key: string, transitions: BoardTransition[]): Promise<void> {
    try {
      await BoardApi.setProjectTransitions(key, transitions);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  // ── Statuses ─────────────────────────────────────────────────────────

  async addStatus(projectKey: string, name: string, category: BoardStatus["category"]): Promise<BoardStatus | null> {
    try {
      const status: BoardStatus = await BoardApi.addStatus(projectKey, name, category);
      await this.refresh({ kind: "config.changed" });
      return status;
    } catch {
      return null;
    }
  }

  async updateStatus(statusId: string, patch: Partial<Pick<BoardStatus, "name" | "category">>): Promise<void> {
    try {
      await BoardApi.updateStatus(statusId, patch.name, patch.category);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async deleteStatus(statusId: string): Promise<void> {
    try {
      await BoardApi.deleteStatus(statusId);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async reorderStatuses(projectKey: string, nextOrder: string[]): Promise<void> {
    try {
      await BoardApi.reorderStatuses(projectKey, nextOrder);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  // ── Labels ───────────────────────────────────────────────────────────

  async ensureLabel(name: string): Promise<LabelDef | null> {
    const trimmed: string = name.trim();
    if (!trimmed) return null;
    try {
      await BoardApi.ensureLabel(trimmed);
      await this.refresh({ kind: "config.changed" });
      return null;
    } catch {
      return null;
    }
  }

  async setLabelColor(name: string, color: string): Promise<void> {
    try {
      await BoardApi.setLabelColor(name, color);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  // ── Versions ─────────────────────────────────────────────────────────

  async createVersion(projectKey: string, name: string): Promise<Version | null> {
    try {
      const version: Version = await BoardApi.createVersion(projectKey, name);
      await this.refresh({ kind: "config.changed" });
      return version;
    } catch {
      return null;
    }
  }

  async updateVersion(
    id: string,
    name: string,
    releaseDate: string | null,
    released: boolean,
    description: string | null
  ): Promise<void> {
    try {
      await BoardApi.updateVersion(id, name, releaseDate, released, description);
    } catch {
      // toasted
    } finally {
      // Always reconcile: confirms the edit, or rolls back the caller's optimistic cache patch if it failed.
      await this.refresh({ kind: "config.changed" });
    }
  }

  async deleteVersion(id: string): Promise<void> {
    try {
      await BoardApi.deleteVersion(id);
    } catch {
      // toasted
    } finally {
      // Always reconcile: confirms the removal, or restores the row if the delete failed.
      await this.refresh({ kind: "config.changed" });
    }
  }

  // ── Custom fields ────────────────────────────────────────────────────

  async createCustomField(
    projectKey: string,
    name: string,
    type: CustomFieldType,
    options: string[] = [],
    issueTypes: IssueType[] = []
  ): Promise<CustomFieldDef | null> {
    try {
      const def: CustomFieldDef = await BoardApi.createCustomField(projectKey, name, type, options, issueTypes);
      await this.refresh({ kind: "config.changed" });
      return def;
    } catch {
      return null;
    }
  }

  async updateCustomField(
    fieldId: string,
    name: string,
    options: string[] = [],
    issueTypes: IssueType[] = []
  ): Promise<CustomFieldDef | null> {
    try {
      const def: CustomFieldDef = await BoardApi.updateCustomField(fieldId, name, options, issueTypes);
      await this.refresh({ kind: "config.changed" });
      return def;
    } catch {
      return null;
    }
  }

  async deleteCustomField(fieldId: string): Promise<void> {
    try {
      await BoardApi.deleteCustomField(fieldId);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async reorderCustomFields(projectKey: string, orderedIds: string[]): Promise<void> {
    try {
      await BoardApi.reorderCustomFields(projectKey, orderedIds);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async setCustomFieldValue(issueId: string, fieldId: string, value: string): Promise<void> {
    try {
      await BoardApi.setCustomFieldValue(issueId, fieldId, value);
      await this.refresh({ kind: "issue.updated", issueId });
    } catch {
      // toasted
    }
  }

  // ── Attachments ──────────────────────────────────────────────────────

  async addAttachment(
    issueId: string,
    fileName: string,
    mimeType: string,
    dataUrl: string,
    sizeBytes: number
  ): Promise<Attachment | null> {
    if (sizeBytes > MAX_ATTACHMENT_BYTES) return null;
    try {
      const attachment: Attachment | null = await BoardApi.addAttachment(
        issueId,
        fileName,
        mimeType,
        dataUrl,
        sizeBytes
      );
      await this.refresh({ kind: "attachments.changed", issueId });
      return attachment;
    } catch {
      return null;
    }
  }

  async removeAttachment(attachmentId: string): Promise<void> {
    try {
      await BoardApi.removeAttachment(attachmentId);
    } catch {
      // toasted
    }
    await this.refresh({ kind: "attachments.changed" });
  }

  // ── Artifact links ───────────────────────────────────────────────────

  async addArtifactLink(
    issueId: string,
    kind: ArtifactKind,
    refId: string,
    displayName: string
  ): Promise<ArtifactLink | null> {
    let link: ArtifactLink | null = null;
    try {
      link = await BoardApi.addArtifactLink(issueId, kind, refId, displayName);
    } catch {
      // toasted
    }
    await this.refresh({ kind: "links.changed", issueId });
    return link;
  }

  async removeArtifactLink(linkId: string): Promise<void> {
    try {
      await BoardApi.removeArtifactLink(linkId);
    } catch {
      // toasted
    }
    await this.refresh({ kind: "links.changed" });
  }

  async createIssueFromArtifact(
    projectName: string,
    artifactKind: ArtifactKind,
    refId: string,
    displayName: string,
    summary: string
  ): Promise<Issue | null> {
    try {
      const issue: Issue | null = await BoardApi.createIssueFromArtifact(
        projectName,
        artifactKind,
        refId,
        displayName,
        summary
      );
      if (issue) await this.refresh({ kind: "issue.created", issueId: issue.id, statusId: issue.statusId });
      return issue;
    } catch {
      return null;
    }
  }

  // ── Automations ──────────────────────────────────────────────────────

  async createAutomation(
    projectKey: string,
    name: string,
    trigger: AutomationTrigger,
    action: AutomationAction,
    notifyOnRun: boolean
  ): Promise<AutomationRule | null> {
    try {
      const rule: AutomationRule = await BoardApi.createAutomation(projectKey, name, trigger, action, notifyOnRun);
      await this.refresh({ kind: "config.changed" });
      return rule;
    } catch {
      return null;
    }
  }

  async toggleAutomation(ruleId: string): Promise<void> {
    try {
      await BoardApi.toggleAutomation(ruleId);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  async deleteAutomation(ruleId: string): Promise<void> {
    try {
      await BoardApi.deleteAutomation(ruleId);
      await this.refresh({ kind: "config.changed" });
    } catch {
      // toasted
    }
  }

  private emit(event: BoardChangeEvent): void {
    for (const listener of this.listeners) {
      listener(event);
    }
  }
}

export const BoardService: BoardServiceImpl = new BoardServiceImpl();
